import { ActionTree, GetterTree, MutationTree } from 'vuex'
import { getApolloQueryClient } from '@/api/apollo-client'
import client from '@/api/client'
import {
    Learning,
    InitLearning,
} from './../../pages/appstore/entities/learning'
import { App, InitApp, LooseObject } from './../../pages/appstore/entities/app'
import { AppstoreInitialData } from '@/pages/appstore/entities/appstore'
import { TenantConfig } from '@/pages/appstore/entities/tenantConfig'
import { StepTip, StepAPIPayload } from '@/pages/appstore/entities/step-tip'
import {
    getAppstore,
    getAppstores,
    getLearnings,
    getLearning,
} from '@/graphql/appstore/appstore'

export interface AppstoreState {
    baseURL: string
    adminBaseURL: string
    graphBaseURL: string
    tenantConfig: TenantConfig[]
    isEmbed: boolean
    enabledBetaAppsString: string
    allApps: App[]
    installedAppsNames: string[]
    curApp: App
    curAppLearnings: Learning[]
    curAppApps: App[]
    curAppSteps: StepTip[]
    allLearnings: Learning[]
    curLearning: Learning
    curLearningLearnings: Learning[]
    curLearningApps: App[]
    csvImporterEmailDomain: string
    searchTerm: string
}

export const state: AppstoreState = {
    baseURL: '',
    adminBaseURL: '',
    graphBaseURL: '',
    tenantConfig: [],
    isEmbed: false,
    enabledBetaAppsString: '',
    allApps: [],
    installedAppsNames: [],
    curApp: { ...InitApp },
    curAppLearnings: [],
    curAppApps: [],
    curAppSteps: [],
    allLearnings: [],
    curLearning: { ...InitLearning },
    curLearningLearnings: [],
    curLearningApps: [],
    csvImporterEmailDomain: '',
    searchTerm: '',
}

// Create the apollo query client
let apolloClient: LooseObject
const actions: ActionTree<AppstoreState, any> = {
    async setAjaxClients({ commit, state }) {
        client.defaults.params.enabledBetaApps = state.enabledBetaAppsString
        apolloClient = getApolloQueryClient(state.graphBaseURL)
    },
    async loadAllApps({ commit, state }) {
        const options = {
            query: getAppstores(),
            variables: { enabledBetaApps: state.enabledBetaAppsString },
        }
        const { data } = await apolloClient.query(options)

        commit('setAllApps', data.appstores)
    },
    async loadInstalledAppsNames({ commit, state }) {
        const { data } = await client.get('/appstore/installedAppsNames')

        commit('setInstalledAppsNames', data.installedAppsNames)
    },
    async loadCurApp({ commit, state }, app) {
        const options = { query: getAppstore(app.id), fetchPolicy: 'no-cache' }
        const { data } = await apolloClient.query(options)

        commit('setCurApp', data.appstore)
        commit('setCurAppLearnings', data.appstore.linked_learning)
        commit('setCurAppApps', data.appstore.linked_appstore)
        commit(
            'setCurAppSteps',
            data.appstore.linked_steps.map(
                ({ learning_id, appstore_id, ...rest }: StepAPIPayload) => ({
                    ...rest,
                    learningId: learning_id,
                    appstoreId: appstore_id,
                })
            )
        )
    },
    // relationship between app & learning can only be editted in learning editor
    async loadAllLearnings({ commit, state }) {
        const options = { query: getLearnings() }
        const { data } = await apolloClient.query(options)

        commit('setAllLearnings', data.learnings)
    },
    async loadCurLearning({ commit, state }, learningId: string) {
        const options = { query: getLearning(learningId) }
        const { data } = await apolloClient.query(options)

        commit('setCurLearning', data.learning)
        commit('setCurLearningApps', data.learning.linked_appstore)
    },
}

const mutations: MutationTree<AppstoreState> = {
    setBasics(state, data: AppstoreInitialData) {
        state.baseURL = data.baseURL
        state.adminBaseURL = data.adminBaseURL
        state.graphBaseURL = data.graphBaseURL
        state.enabledBetaAppsString = data.enabledBetaApps
        state.tenantConfig = data.tenantConfig
        state.isEmbed = data.isEmbed
        state.csvImporterEmailDomain = data.csvImporterEmailDomain
    },
    setAllApps(state, apps) {
        state.allApps = apps
    },
    setInstalledAppsNames(state, installedAppsNames: string[]) {
        state.installedAppsNames = installedAppsNames
    },
    setAllLearnings(state, learnings) {
        state.allLearnings = state.allLearnings.concat(learnings)
    },
    setCurApp(state, app: App) {
        state.curApp = app
    },
    setCurLearning(state, learning: Learning) {
        state.curLearning = { ...learning }
    },

    setCurLearningApps(state, apps: App[]) {
        state.curLearningApps = apps
    },
    setCurAppLearnings(state, learnings: Learning[]) {
        state.curAppLearnings = learnings
    },
    setCurAppApps(state, apps: App[]) {
        state.curAppApps = apps
    },
    setCurAppSteps(state, steps: StepTip[]) {
        state.curAppSteps = steps
    },
    setSearchTerm(state, searchTerm: string) {
        state.searchTerm = searchTerm
    },
}

const getters: GetterTree<AppstoreState, any> = {
    getTenantConfig: ({ tenantConfig }) => tenantConfig,
    getIsEmbed: ({ isEmbed }) => isEmbed,
    getAllApps: ({ allApps }) => allApps,
    getInstalledApps: ({ allApps, installedAppsNames }) =>
        allApps.filter((e: App) => installedAppsNames.includes(e.name)),
    getInstalledAppsNames: ({ installedAppsNames }) => installedAppsNames,
    getCurApp: ({ curApp }) => curApp,
    getCurAppLearnings: ({ curAppLearnings }) => curAppLearnings,
    getCurAppLearningsIds: ({ curAppLearnings }) =>
        curAppLearnings.map((e: Learning) => e.id),
    getCurAppApps: ({ curAppApps }) => curAppApps,
    getCurAppAppsIds: ({ curAppApps }) => curAppApps.map((e: App) => e.id),
    getAllLearnings: ({ allLearnings }) => allLearnings,
    getCurLearning: ({ curLearning }) => curLearning,
    getCurLearningLearnings: ({ curLearningLearnings }) => curLearningLearnings,
    getCurLearningApps: ({ curLearningApps }) => curLearningApps,
    getCurLearningAppsIds: ({ curLearningApps }) =>
        curLearningApps.map((e: App) => e.id),
    getCurAppSteps: ({ curAppSteps }) => curAppSteps,
    baseURL: ({ baseURL }) => baseURL,
    csvImporterEmailDomain: ({ csvImporterEmailDomain }) =>
        csvImporterEmailDomain,
    getSearchTerm: ({ searchTerm }) => searchTerm,
}

export default {
    namespaced: false,
    state,
    actions,
    mutations,
    getters,
}
